import { getWorkItemResources } from '@jira/router-resources__work-item-view/src/workItemResourcesRouteResource.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { WorkItemViewSkeleton } from '@jira/work-item__work-item-view-skeleton/src/WorkItemViewSkeleton.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { getNavigationResourcesForWorkItem } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { workItemConditionalListResources } from '@jira/router-resources__work-item-conditional-list/src/workItemConditionalListResources.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { issueDescriptionSmartlinkDataResource } from '@jira/router-resources__issue-view/src/issueDescriptionSmartlinkDataResource.tsx';
import { projectThemeResource } from '@jira/router-resources__project-theme/src/projectThemeResource.tsx';
import { genericProjectLayout } from '@jira/platform__layouts-generic-project-layout/src/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { workItemRoute } from '@jira/router-routes__work-item-routes/src/workItemRoute.tsx';
import { loomPermissionResource } from '@jira/router-resources__loom/src/loomPermissionResource.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import type { Route } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { workItemPageEntrypoint } from './ui/index.tsx';
import { workItemRedirectionHandler } from './common/workItemRedirectionHandler.tsx';

export const workItemRouteEntry: Route = createEntry(workItemRoute, () => ({
	group: 'work-item',
	navigation: {
		menuId: MenuIdProjects,
	},
	// bifrost property is set up, which is required for Bifrost SSR onboarding
	bifrost: {
		bifrostGroup: 'issue-view',
		manifestName: 'jira-spa-issue-view.json',
		redirects: [
			{
				key: 'work-item-project-context-redirect',
				timeoutMs: 1000,
				handler: workItemRedirectionHandler,
			},
		],
	},
	meta: {
		capability: 'WORK_ITEM_VIEW',
		reporting: {
			id: 'routeApp.issue',
			packageName: 'jiraSpa',
			teamName: 'bento',
		},
	},
	resources: [
		...getIssueViewResources({ disableShadowQuery: true }),
		...getNavigationResourcesForWorkItem(),
		// eslint-disable-next-line jira/router/no-project-context-resource
		projectContextResource,
		projectThemeResource,
		// Required for useFaviconService to resolve the project avatar and display it as the
		// browser tab favicon — matching the behaviour of the old issue view (JIV).
		staticProjectSidebarNavigationResource,
		// List view resources (loaded when ?hasList=true)
		workItemConditionalListResources.conditionalIssueNavigatorResource,
		workItemConditionalListResources.conditionalFieldsResource,
		workItemConditionalListResources.conditionalProjectContextResource,
		...getWorkItemResources(),
		issueDescriptionSmartlinkDataResource,
		loomPermissionResource,
	],
	// Compose the project layout so the Quickstart onboarding panel (injected via the layout's
	// rightSidebars/globalComponents slots) renders in the Work Item View, matching the classic
	// issue route (browseIssueRouteEntry).
	layout: genericProjectLayout,
	skeleton: WorkItemViewSkeleton,
	entryPoint: () => workItemPageEntrypoint,
	ufoName: 'work-item',
	fallbackExperience: 'work-item-view',
	supportsPreviewPanel: true,
	supportsProjectTheme: 'contoured',
	isHydrateRootEnabled: true,
	isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-work-item'),
	excludeFromWrmDeferredLoading: true,
}));
